import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { BRANDS } from "@/data/products";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
});

const Hero = () => (
  <section data-testid="hero-section" className="text-center px-4 pt-20 pb-20 sm:pt-28 sm:pb-24">
    <motion.span
      {...fade(0)}
      className="inline-block rounded-full border border-white/15 px-4 py-1.5 text-[11px] uppercase tracking-[0.25em] text-zinc-400"
      data-testid="hero-status-pill"
    >
      ✨ Three Brands, One Home.
    </motion.span>
    <motion.h1
      {...fade(0.15)}
      className="mt-8 font-extrabold tracking-tight leading-[1.02] text-6xl sm:text-7xl lg:text-8xl text-white"
      data-testid="hero-headline"
    >
      Glow.
      <br />
      <em className="font-serif2 italic font-semibold bg-gradient-to-r from-pink-400 via-rose-400 to-amber-300 bg-clip-text text-transparent">
        Bling.
      </em>{" "}
      Sip.
    </motion.h1>
    <motion.p {...fade(0.3)} className="mt-6 text-zinc-400 max-w-xl mx-auto leading-relaxed" data-testid="hero-subcopy">
      From custom jewelry that turns heads to skincare that turns skin, and lemonade that turns
      any day into summer — welcome to the family.
    </motion.p>
  </section>
);

const BrandCard = ({ num, label, name, desc, to, className, nameClass, descClass, linkClass, flyer, flyerClass, delay }) => (
  <motion.div {...fade(delay)}>
    <Link
      to={to}
      data-testid={`brand-card-${label.toLowerCase()}`}
      className={`group relative block rounded-3xl overflow-hidden p-8 sm:p-10 min-h-[300px] ${className}`}
    >
      <p className="text-xs uppercase tracking-[0.25em] opacity-70">{num} — {label}</p>
      <h3 className={`mt-4 text-4xl sm:text-5xl ${nameClass}`}>{name}</h3>
      <p className={`mt-3 max-w-[55%] text-sm leading-relaxed ${descClass}`}>{desc}</p>
      <span className={`absolute bottom-8 left-8 sm:left-10 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] ${linkClass}`}>
        Shop {label}
        <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
      </span>
      <img
        src={flyer}
        alt={`${name} flyer`}
        loading="lazy"
        className={`absolute pointer-events-none ${flyerClass}`}
      />
    </Link>
  </motion.div>
);

const BrandCards = () => (
  <section data-testid="brands-grid" className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-6 pb-20">
    <BrandCard
      num="01" label="Bling" name="Bourne For Bling"
      desc="Handcrafted jewelry, beaded bracelets, statement pieces. Made to shine."
      to="/bling" delay={0.1}
      className="bg-gradient-to-br from-purple-600 via-purple-700 to-purple-900 text-white"
      nameClass="font-serif2 font-semibold text-white"
      descClass="text-purple-100/90"
      linkClass="text-white"
      flyer={BRANDS.bling.flyer}
      flyerClass="bottom-0 right-0 w-1/2 max-w-[240px] rounded-tl-2xl opacity-90"
    />
    <BrandCard
      num="02" label="Glow" name="Bourne To Glow"
      desc="Luxury skincare, whipped body butters, hair oils. Glow head to toe."
      to="/glow" delay={0.2}
      className="bg-[#141414] border border-white/10 text-white"
      nameClass="font-serif2 font-semibold text-white"
      descClass="text-zinc-400"
      linkClass="text-white"
      flyer={BRANDS.glow.flyer}
      flyerClass="bottom-0 right-0 w-1/2 max-w-[220px] rounded-tl-2xl opacity-80"
    />
    <div className="lg:col-span-2">
      <BrandCard
        num="03" label="Lemonade" name="Lani Lemonade"
        desc="Fresh, cold, made with love. 34oz cups. 20+ flavors. Delivery available."
        to="/lemonade" delay={0.3}
        className="bg-gradient-to-r from-pink-400 via-rose-400 to-orange-300"
        nameClass="font-extrabold tracking-tight text-[#3b0a2a]"
        descClass="text-[#3b0a2a]/80"
        linkClass="text-[#3b0a2a]"
        flyer={BRANDS.lemonade.flyer}
        flyerClass="bottom-4 right-4 sm:right-8 w-1/2 max-w-[380px] rounded-2xl shadow-2xl hidden sm:block"
      />
    </div>
  </section>
);

const Features = () => (
  <section data-testid="features-section" className="max-w-6xl mx-auto px-4 sm:px-6 grid sm:grid-cols-3 gap-6 pb-24">
    {[
      { title: "One Checkout", text: "Add items from all 3 brands. Pay once via Square." },
      { title: "Every Payment Method", text: "Card, Afterpay, Cash App, Apple Pay, Google Pay." },
      { title: "Made With Love", text: "Small-batch, handcrafted, black-owned & Bourne to shine." },
    ].map((f, i) => (
      <motion.div
        key={f.title}
        {...fade(0.1 + i * 0.1)}
        className="rounded-2xl border border-white/10 bg-[#141414] p-6"
        data-testid={`feature-card-${i}`}
      >
        <h3 className="font-semibold text-white">{f.title}</h3>
        <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{f.text}</p>
      </motion.div>
    ))}
  </section>
);

export default function Home() {
  return (
    <main>
      <Hero />
      <BrandCards />
      <Features />
    </main>
  );
}
